import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import AddProduct from './AddProduct';
import FilesModal from './FilesModal';
import InkNotesModal from './InkNotesModal';
import PressSetupModal from './PressSetupModal';
import PressSetupIndicatorButton from './PressSetupIndicatorButton';
import {
  Box,
  Paper,
  Grid,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Divider,
  Tooltip,
  InputLabel,
  FormControl,
  InputAdornment,
  Collapse,
  Card,
  CardContent,
  Button,
  Checkbox
} from '@mui/material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ColorizeIcon from '@mui/icons-material/Colorize';
import { collection, getDocs, addDoc, doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { 
  SIZES,
  LINE_TYPES,
  LINE_STATUSES,
  sortSizes
} from '../firebase/schema';
import { getMockupUrl, getPersistableMockupUrl } from '../utils/fileHelpers';
import {
  upsertCustomerDesignMockup
} from '../utils/designMockupIndex';
import {
  fetchCustomerDesignLibrary,
  filterCustomerDesignLibrary,
  applyDesignTemplateToLine,
  applyLibraryMockupToLine,
  buildLineSnapshotFromLine,
  getDesignLibrarySubtitle,
  invalidateCustomerDesignLibrary
} from '../utils/customerDesignLibrary';
import CustomerMockupsModal from './CustomerMockupsModal';

// Styled components
// Create theme with spacing function
const theme = createTheme({
  spacing: (factor) => `${8 * factor}px`,
  palette: {
    mode: 'dark',
    primary: {
      main: '#98FB98'
    },
    background: {
      paper: '#2C2C2C',
      default: '#1C1C1C'
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)'
    }
  }
});

const StyledCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  backgroundColor: 'var(--al-surface)',
  marginBottom: theme.spacing(2),
  borderRadius: 0,
  border: '1px solid rgba(255, 255, 255, 0.1)'
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    color: 'white',
    fontSize: '0.75rem',
    backgroundColor: '#363636',
    borderRadius: 0,
    height: '32px',
    '&:hover': {
      backgroundColor: '#404040'
    }
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.65rem',
    transform: 'translate(14px, 8px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    }
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.1)'
  },
  '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--al-primary)'
  },
  '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--al-primary)'
  },
  '& .MuiInputBase-input': {
    padding: '6px 10px',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0
    },
    '&[type=number]': {
      MozAppearance: 'textfield'
    }
  }
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: '#363636',
  color: 'white',
  fontSize: '0.75rem',
  height: '32px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.1)'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--al-primary)'
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--al-primary)'
  },
  '& .MuiSelect-select': {
    padding: '6px 10px'
  }
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.65rem',
    transform: 'translate(14px, 8px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
      backgroundColor: 'var(--al-surface)',
      padding: '0 4px'
    }
  }
}));

const ArtworkUploadBox = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100px',
  border: '2px dashed rgba(255, 255, 255, 0.1)',
  borderRadius: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover, &.dragging': {
    borderColor: 'var(--al-primary)',
    backgroundColor: 'rgba(var(--al-primary-rgb), 0.05)'
  },
  '&.dragging': {
    backgroundColor: 'rgba(var(--al-primary-rgb), 0.1)'
  }
}));

const SizeGrid = styled(Grid)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(9, 52px)',
  gap: '4px',
  alignItems: 'center'
}));

const MainGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '3fr 2fr 2fr 2fr 56px',
  gap: '6px',
  padding: '10px',
  alignItems: 'start'
}));

const ReorderRail = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0,
  padding: '10px 0',
  minWidth: '32px',
  alignSelf: 'stretch'
}));

const reorderIconButtonSx = {
  color: 'white',
  padding: 0,
  height: '28px',
  width: '28px',
  '&.Mui-disabled': {
    color: 'rgba(255, 255, 255, 0.2)'
  },
  '&:hover:not(.Mui-disabled)': {
    color: 'var(--al-primary)'
  }
};

const PrintLocationsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '120px repeat(3, 140px)',
  gap: '10px',
  marginTop: '6px',
  alignItems: 'start'
}));

const RightSideGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '110px 85px 85px',
  gap: '6px',
  alignItems: 'start'
}));

const ColorInputsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  marginTop: '4px'
}));

const StyleSearchContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%'
}));

const StyleSearchResults = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  background: '#363636',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderTop: 'none',
  maxHeight: '200px',
  overflowY: 'auto',
  zIndex: 1000
}));

const StyleSearchResult = styled(Box)(({ theme }) => ({
  padding: '8px 12px',
  cursor: 'pointer',
  fontSize: '0.75rem',
  '&:hover': {
    background: 'var(--al-primary)',
    color: '#000000'
  }
}));

const InkSearchContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%'
}));

const InkSearchResults = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  background: '#363636',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderTop: 'none',
  maxHeight: '200px',
  overflowY: 'auto',
  zIndex: 1000
}));

const InkSearchResult = styled(Box)(({ theme }) => ({
  padding: '8px 12px',
  cursor: 'pointer',
  fontSize: '0.75rem',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '&:hover': {
    background: 'var(--al-primary)',
    color: '#000000'
  }
}));

const ColorSwatch = styled(Box)(({ hex }) => ({
  width: '16px',
  height: '16px',
  borderRadius: '2px',
  backgroundColor: hex || '#ccc',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  flexShrink: 0
}));

const LocationCheckboxContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px'
}));

const SharpCheckbox = styled('input')`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border: 2px solid var(--al-primary);
  background: transparent;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  
  &:checked {
    background: var(--al-primary);
    
    &:after {
      content: "×";
      color: var(--al-bg);
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      font-size: 14px;
      font-weight: bold;
      line-height: 1;
    }
  }

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: none;
    border-color: var(--al-primary);
  }
`;

const VerificationIndicator = styled(Box)(({ verified, needsArtwork }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '0.7rem',
  color: verified ? 'var(--al-primary)' : needsArtwork ? '#FFC107' : 'rgba(255, 255, 255, 0.5)',
  fontWeight: verified || needsArtwork ? 600 : 400
}));

const UploadFilesButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '120px',
  backgroundColor: '#363636',
  color: 'var(--al-primary)',
  border: '1px solid rgba(var(--al-primary-rgb), 0.3)',
  borderRadius: 0,
  fontFamily: "'Montserrat', sans-serif",
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  '&:hover': {
    backgroundColor: '#404040',
    borderColor: 'var(--al-primary)'
  }
}));

const AddNewButton = styled(Button)(({ theme }) => ({
  width: '100%',
  padding: '8px 12px',
  background: 'var(--al-primary)',
  color: '#000000',
  border: 'none',
  textAlign: 'left',
  cursor: 'pointer',
  fontSize: '0.75rem',
  textTransform: 'none',
  '&:hover': {
    background: '#7FE87F'
  }
}));

// MenuProps so MUI Select dropdowns render above modals (e.g. Edit Line modal on Lines page)
const SELECT_MENU_PROPS = {
  MenuProps: {
    sx: { zIndex: 10000 },
    PaperProps: { sx: { zIndex: 10000 } }
  }
};

// Map DB/stored type (e.g. 'screen', 'design_only') to LINE_TYPES display value
const normalizePrintType = (type) => {
  if (!type) return LINE_TYPES.SCREEN_PRINT;
  const t = String(type).toLowerCase();
  if (t === 'screen' || t === 'screen print') return LINE_TYPES.SCREEN_PRINT;
  if (t === 'embroidery') return LINE_TYPES.EMBROIDERY;
  if (t === 'dtg') return LINE_TYPES.DTG;
  if (t === 'dtf') return LINE_TYPES.DTF;
  if (t === 'design_only' || t === 'design only') return LINE_TYPES.DESIGN_ONLY;
  // If it's already a valid LINE_TYPES value, return as-is
  if (Object.values(LINE_TYPES).includes(type)) return type;
  return LINE_TYPES.SCREEN_PRINT;
};

const normalizeSizeTypeForState = (sizeType) => {
  if (!sizeType) return 'ADULT';
  if (SIZES[sizeType]) return sizeType;
  const normalized = String(sizeType).toUpperCase();
  if (SIZES[normalized]) return normalized;
  return 'ADULT';
};

// Treat Pantone/HEX entries as intentional "exact mix targets"
const isHexTarget = (value) => {
  if (!value) return false;
  const v = String(value).trim();
  return /^#?[0-9a-f]{3}$|^#?[0-9a-f]{6}$|^#?[0-9a-f]{8}$/i.test(v);
};

const isPantoneTarget = (value) => {
  if (!value) return false;
  const v = String(value).trim();
  // Examples: "PANTONE 186 C", "PMS 186 C", "186 C", "186C"
  return /^(pantone|pms)\s*[0-9]{3,4}\s*[a-z]{0,2}$/i.test(v) || /^[0-9]{3,4}\s*[a-z]{0,2}$/i.test(v);
};

const isExactMixTarget = (value) => isHexTarget(value) || isPantoneTarget(value);

const PANTONE_TO_HEX = {
  'BLACK 6 C': '#101820',
  'BLUE 072 C': '#0018A8',
  'PROCESS BLUE C': '#0085CA',
  'RED 032 C': '#EF3340',
  'YELLOW C': '#FFD700',
  'GREEN C': '#00AB84',
  'ORANGE 021 C': '#FF5F00',
  'PURPLE C': '#BB29BB',
  'WHITE C': '#FFFFFF',
  'COOL GRAY 1 C': '#D9D9D6',
  'COOL GRAY 5 C': '#B1B3B3',
  'WARM GRAY 1 C': '#D7D2CB',
  'WARM GRAY 5 C': '#B6B1A9',
  'RHODAMINE RED C': '#E10098',
  'RUBINE RED C': '#CE0058',
  'VIOLET C': '#440099',
  // Common shorthand inputs (mapped best-effort)
  'PMS 186 C': '#C8102E',
  '186 C': '#C8102E',
  '186C': '#C8102E'
};

const normalizeHex = (hex) => {
  if (!hex) return null;
  let h = String(hex).trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length === 8) h = h.slice(0, 6);
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return `#${h.toUpperCase()}`;
};

const resolveTargetHex = (value) => {
  const raw = String(value || '').trim();
  const asHex = normalizeHex(raw);
  if (asHex) return asHex;
  const normalizedPantone = raw.toUpperCase().replace(/^PANTONE\s+/, '').replace(/\s+/g, ' ').trim();
  return PANTONE_TO_HEX[normalizedPantone] || PANTONE_TO_HEX[raw.toUpperCase()] || null;
};

const hexToRgb = (hex) => {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;
  const h = normalized.slice(1);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16)
  };
};

const rgbToLab = ({ r, g, b }) => {
  const toLinear = (v) => {
    const c = v / 255;
    return c > 0.04045 ? ((c + 0.055) / 1.055) ** 2.4 : c / 12.92;
  };
  const R = toLinear(r);
  const G = toLinear(g);
  const B = toLinear(b);
  const X = (R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047;
  const Y = (R * 0.2126 + G * 0.7152 + B * 0.0722) / 1.0;
  const Z = (R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883;
  const f = (t) => (t > 0.008856 ? t ** (1 / 3) : 7.787 * t + 16 / 116);
  const fx = f(X);
  const fy = f(Y);
  const fz = f(Z);
  return {
    L: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz)
  };
};

const deltaE76 = (lab1, lab2) => {
  const dL = lab1.L - lab2.L;
  const da = lab1.a - lab2.a;
  const db = lab1.b - lab2.b;
  return Math.sqrt(dL * dL + da * da + db * db);
};

const LineItem2 = ({
  lineData: rawLineData = {},
  index,
  onUpdate,
  onDelete,
  onDuplicate = () => {},
  onMoveUp,
  onMoveDown,
  canMoveUp = false,
  canMoveDown = false,
  orderNumber = null,
  lineId = null,
  customerId = null,
  orderId = null
}) => {
  // Normalize lineData: ensure type is a LINE_TYPES value (DB may store 'screen', etc.)
  const lineData = {
    ...rawLineData,
    type: normalizePrintType(rawLineData.type) || LINE_TYPES.SCREEN_PRINT
  };
  
  const [expanded, setExpanded] = useState(lineData.isExpanded || false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedSizeType, setSelectedSizeType] = useState(lineData.sizeType || 'ADULT');
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState(lineData.style || '');
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [artworkPreviewUrl, setArtworkPreviewUrl] = useState(lineData.artworkUrl || lineData.dtgArtworkUrl);
  const [inks, setInks] = useState([]);
  const [threads, setThreads] = useState([]);
  const [filteredInks, setFilteredInks] = useState({});
  const [inkSearchQueries, setInkSearchQueries] = useState({});
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [showMockupLibrary, setShowMockupLibrary] = useState(false);
  const [showInkNotesModal, setShowInkNotesModal] = useState(false);
  const [showPressSetupModal, setShowPressSetupModal] = useState(false);
  const [customerDesignLibrary, setCustomerDesignLibrary] = useState([]);
  const [filteredDesignLibrary, setFilteredDesignLibrary] = useState([]);
  const [showDesignLibrary, setShowDesignLibrary] = useState(false);
  const [designLibraryLoading, setDesignLibraryLoading] = useState(false);
  const inkSearchTimeoutRef = useRef({});
  const designLibraryBlurTimeoutRef = useRef(null);

  // Update preview URL when lineData changes. Use raw URL (including blob) for in-session preview
  // so dropped files show immediately; getMockupUrl() filters out blobs for persistence only.
  useEffect(() => {
    const raw =
      lineData.files?.mockup?.url ||
      lineData.designFileUrl ||
      lineData.mockupUrl ||
      lineData.artworkUrl ||
      null;
    setArtworkPreviewUrl(raw || null);
  }, [lineData.files?.mockup?.url, lineData.designFileUrl, lineData.mockupUrl, lineData.artworkUrl]);

  const designName = lineData.design || lineData.designName || '';

  useEffect(() => {
    if (lineData.isExpanded) {
      setExpanded(true);
    }
  }, [lineData.isExpanded]);

  useEffect(() => {
    let cancelled = false;

    if (!customerId) {
      setCustomerDesignLibrary([]);
      setFilteredDesignLibrary([]);
      return undefined;
    }

    setDesignLibraryLoading(true);
    fetchCustomerDesignLibrary(customerId)
      .then((entries) => {
        if (!cancelled) setCustomerDesignLibrary(entries);
      })
      .catch((error) => {
        console.error('Error loading customer design library:', error);
        if (!cancelled) setCustomerDesignLibrary([]);
      })
      .finally(() => {
        if (!cancelled) setDesignLibraryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [customerId]);

  useEffect(() => {
    if (!showDesignLibrary || !designName.trim() || designName.trim().length < 2) {
      setFilteredDesignLibrary([]);
      return;
    }
    setFilteredDesignLibrary(filterCustomerDesignLibrary(customerDesignLibrary, designName));
  }, [customerDesignLibrary, designName, showDesignLibrary]);

  const syncDesignMockupIndex = useCallback(async (url, fileName = null) => {
    if (!customerId || !designName.trim() || !getPersistableMockupUrl(url)) return;

    try {
      await upsertCustomerDesignMockup({
        customerId,
        designName: designName.trim(),
        mockupUrl: url,
        mockupFileName: fileName,
        sourceLineId: lineId || lineData.docId || lineData.id || null,
        sourceOrderId: orderId,
        sourceOrderNumber: orderNumber,
        lineSnapshot: buildLineSnapshotFromLine(lineData)
      });
      invalidateCustomerDesignLibrary(customerId);
      const refreshed = await fetchCustomerDesignLibrary(customerId, { forceRefresh: true });
      setCustomerDesignLibrary(refreshed);
    } catch (error) {
      console.error('Error updating design mockup index:', error);
    }
  }, [customerId, designName, lineId, lineData, orderId, orderNumber]);

  const handleDesignNameChange = (value) => {
    onUpdate({ ...lineData, design: value });
    setShowDesignLibrary(true);
  };

  const handleDesignLibrarySelect = async (entry) => {
    if (!entry) return;

    if (designLibraryBlurTimeoutRef.current) {
      clearTimeout(designLibraryBlurTimeoutRef.current);
      designLibraryBlurTimeoutRef.current = null;
    }

    let enrichedEntry = entry;
    if (!entry.lineSnapshot && entry.sourceLineId) {
      try {
        const lineDoc = await getDoc(doc(db, 'orderLines', entry.sourceLineId));
        if (lineDoc.exists()) {
          enrichedEntry = {
            ...entry,
            lineSnapshot: buildLineSnapshotFromLine(lineDoc.data())
          };
        }
      } catch (error) {
        console.error('Error loading design template line:', error);
      }
    }

    let updatedLine = applyDesignTemplateToLine(lineData, enrichedEntry);

    const styleForMatch = (updatedLine.style || '').trim();
    if (styleForMatch && products.length) {
      const alreadyFull = products.some(
        (p) => `${(p.styleNo || '')} - ${p.shortDescription || p.description || ''}`.trim() === styleForMatch
      );
      if (!alreadyFull) {
        const matched = products.find(
          (p) => (p.styleNo || '').trim().toLowerCase() === styleForMatch.toLowerCase()
        );
        if (matched) {
          const fullStyleDisplay = `${matched.styleNo} - ${matched.shortDescription || matched.description}`;
          const defaultSizeGrid = (matched.defaultSizeGrid || '').trim();
          const sizeType = defaultSizeGrid && SIZES[defaultSizeGrid]
            ? defaultSizeGrid
            : normalizeSizeTypeForState(updatedLine.sizeType);
          const sizes = defaultSizeGrid && SIZES[defaultSizeGrid]
            ? Object.fromEntries(SIZES[defaultSizeGrid].map((size) => [size, updatedLine.sizes?.[size] ?? '']))
            : updatedLine.sizes || {};
          updatedLine = {
            ...updatedLine,
            style: fullStyleDisplay,
            brand: matched.brand || '',
            styleNo: matched.styleNo || '',
            cogs: matched.cogs ?? updatedLine.cogs,
            sizeType,
            sizes
          };
        }
      }
    }

    onUpdate(updatedLine);

    if (updatedLine.style) setSearchQuery(updatedLine.style);
    if (enrichedEntry.mockupUrl) setArtworkPreviewUrl(enrichedEntry.mockupUrl);
    if (updatedLine.sizeType) setSelectedSizeType(normalizeSizeTypeForState(updatedLine.sizeType));
    setShowDesignLibrary(false);
    setFilteredDesignLibrary([]);
    setExpanded(true);
  };

  const persistLibraryMockup = async (entry, updatedLine) => {
    const storageId = lineId || lineData.docId || lineData.id;
    if (!storageId || !entry?.mockupUrl) return;
    try {
      await updateDoc(doc(db, 'orderLines', storageId), {
        'files.mockup': {
          url: entry.mockupUrl,
          fileName: entry.mockupFileName || null
        },
        designFileUrl: entry.mockupUrl,
        ...(updatedLine.design ? { design: updatedLine.design } : {}),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error attaching library mockup:', error);
    }
  };

  const handleSelectLibraryMockup = async (entry) => {
    if (!entry?.mockupUrl) return;
    const updatedLine = applyLibraryMockupToLine(lineData, entry);
    onUpdate(updatedLine);
    setArtworkPreviewUrl(entry.mockupUrl);
    await persistLibraryMockup(entry, updatedLine);
  };

  // Fetch products on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      const productsRef = collection(db, 'products');
      const snapshot = await getDocs(productsRef);
      const productsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsList);
    };

    fetchProducts();
  }, []);

  // When products load, if line style matches a product by styleNo (e.g. imported "3600SW"), auto-resolve to that product
  const styleForMatch = (lineData.style || '').trim();
  const lastResolvedStyleRef = useRef(null);
  useEffect(() => {
    if (!products.length || !styleForMatch) return;
    const fullDisplay = products.some(
      p => `${(p.styleNo || '')} - ${p.shortDescription || p.description || ''}`.trim() === styleForMatch
    );
    if (fullDisplay) {
      lastResolvedStyleRef.current = styleForMatch;
      return;
    }
    const matched = products.find(
      p => (p.styleNo || '').trim().toLowerCase() === styleForMatch.toLowerCase()
    );
    if (matched) {
      lastResolvedStyleRef.current = styleForMatch;
      const fullStyleDisplay = `${matched.styleNo} - ${matched.shortDescription || matched.description}`;
      const defaultSizeGrid = (matched.defaultSizeGrid || '').trim();
      const sizeType = defaultSizeGrid && SIZES[defaultSizeGrid] ? defaultSizeGrid : (lineData.sizeType || 'ADULT');
      const sizes = (defaultSizeGrid && SIZES[defaultSizeGrid])
        ? Object.fromEntries(SIZES[defaultSizeGrid].map(size => [size, lineData.sizes?.[size] ?? '']))
        : lineData.sizes || {};
      setSearchQuery(fullStyleDisplay);
      setSelectedSizeType(sizeType);
      onUpdate({
        ...lineData,
        style: fullStyleDisplay,
        brand: matched.brand || '',
        styleNo: matched.styleNo || '',
        cogs: matched.cogs ?? lineData.cogs,
        sizeType,
        sizes
      });
    } else {
      lastResolvedStyleRef.current = null;
    }
  }, [products, styleForMatch]);

  // Fetch inks on component mount
  useEffect(() => {
    const fetchInks = async () => {
      const inksRef = collection(db, 'inks');
      const snapshot = await getDocs(inksRef);
      const inksList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      inksList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setInks(inksList);
    };

    fetchInks();
  }, []);

  useEffect(() => {
    const fetchThreads = async () => {
      const threadsRef = collection(db, 'threads');
      const snapshot = await getDocs(threadsRef);
      const threadsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      threadsList.sort((a, b) => (a.threadNumber || '').localeCompare(b.threadNumber || '', undefined, { numeric: true }));
      setThreads(threadsList);
    };

    fetchThreads();
  }, []);

  // Handle ink search
  const handleInkSearch = (location, colorIndex, value) => {
    const key = `${location}-${colorIndex}`;
    const isEmbroidery = lineData.type === LINE_TYPES.EMBROIDERY;
    const lookupItems = isEmbroidery ? threads : inks;
    
    // Clear existing timeout
    if (inkSearchTimeoutRef.current[key]) {
      clearTimeout(inkSearchTimeoutRef.current[key]);
    }

    // Update search query
    setInkSearchQueries(prev => ({
      ...prev,
      [key]: value
    }));

    if (value.length === 0) {
      setFilteredInks(prev => {
        const newFiltered = { ...prev };
        delete newFiltered[key];
        return newFiltered;
      });
      return;
    }

    let filtered;
    const targetHex = resolveTargetHex(value);
    if (targetHex && (isHexTarget(value) || isPantoneTarget(value))) {
      // Color matcher mode: rank inventory inks/threads by Delta-E from target HEX
      const targetRgb = hexToRgb(targetHex);
      const targetLab = targetRgb ? rgbToLab(targetRgb) : null;
      filtered = targetLab
        ? lookupItems
            .map((item) => {
              const itemRgb = hexToRgb(item.hexCode);
              if (!itemRgb) return null;
              const itemLab = rgbToLab(itemRgb);
              return { ...item, _deltaE: deltaE76(targetLab, itemLab) };
            })
            .filter(Boolean)
            .sort((a, b) => a._deltaE - b._deltaE)
            .slice(0, 25)
        : [];
    } else {
      const q = value.toLowerCase();
      // Default mode: ink name lookup OR thread number/name lookup
      filtered = lookupItems.filter((item) => {
        const nameMatch = item.name?.toLowerCase().includes(q);
        if (!isEmbroidery) return nameMatch;
        const threadNumberMatch = String(item.threadNumber || '').toLowerCase().includes(q);
        const hexMatch = String(item.hexCode || '').toLowerCase().includes(q);
        return nameMatch || threadNumberMatch || hexMatch;
      });
    }

    setFilteredInks(prev => ({
      ...prev,
      [key]: filtered
    }));
  };

  // Handle ink select
  const handleInkSelect = (location, colorIndex, ink) => {
    const isEmbroidery = lineData.type === LINE_TYPES.EMBROIDERY;
    const colorsArrayKey = `${location.toLowerCase()}InkColors`;
    const newColors = [...(lineData[colorsArrayKey] || [])];
    newColors[colorIndex] = isEmbroidery ? String(ink.threadNumber || ink.name || '').toUpperCase() : ink.name;
    
    onUpdate({
      ...lineData,
      [colorsArrayKey]: newColors
    });

    // Clear search
    const key = `${location}-${colorIndex}`;
    setInkSearchQueries(prev => {
      const newQueries = { ...prev };
      delete newQueries[key];
      return newQueries;
    });
    setFilteredInks(prev => {
      const newFiltered = { ...prev };
      delete newFiltered[key];
      return newFiltered;
    });
  };

  const applyInkInputValue = (location, colorIndex, value) => {
    const colorsArrayKey = `${location.toLowerCase()}InkColors`;
    const newColors = [...(lineData[colorsArrayKey] || [])];
    newColors[colorIndex] = value;
    onUpdate({
      ...lineData,
      [colorsArrayKey]: newColors
    });

    // Trigger inline lookup (name mode or Delta-E mode)
    const isScreenPrint = !lineData.type || lineData.type === LINE_TYPES.SCREEN_PRINT || lineData.type === 'screen' || lineData.type === 'Screen Print';
    const isEmbroidery = lineData.type === LINE_TYPES.EMBROIDERY;
    if ((isScreenPrint && inks.length > 0) || (isEmbroidery && threads.length > 0)) {
      handleInkSearch(location, colorIndex, value);
    }
  };

  const handlePickColor = async (location, colorIndex) => {
    try {
      if (!window.EyeDropper) {
        alert('Eyedropper is not supported in this browser.');
        return;
      }
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      const pickedHex = result?.sRGBHex || '';
      if (!pickedHex) return;
      applyInkInputValue(location, colorIndex, pickedHex.toUpperCase());
    } catch (error) {
      // User cancel is expected; avoid noisy errors
      if (error && error.name !== 'AbortError') {
        console.error('Eyedropper error:', error);
      }
    }
  };

  const handleStyleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.trim() === '') {
      setFilteredProducts([]);
      onUpdate({ ...lineData, style: '' });
      return;
    }

    const filtered = products.filter(product => {
      const styleMatch = product?.styleNo?.toLowerCase()?.includes(query.toLowerCase()) || false;
      const descMatch = (product?.description || '').toLowerCase().includes(query.toLowerCase()) || false;
      const shortDescMatch = (product?.shortDescription || '').toLowerCase().includes(query.toLowerCase()) || false;
      return styleMatch || descMatch || shortDescMatch;
    });

    setFilteredProducts(filtered);
    
    // Only show "Add Product" if there are no matches and we're not displaying a known product
    // (full display "StyleNo - Description" OR exact styleNo e.g. "3600SW" from imports)
    const fullDisplayMatch = products.some(
      p => `${(p.styleNo || '')} - ${p.shortDescription || p.description || ''}`.trim() === query.trim()
    );
    const exactStyleNoMatch = products.some(
      p => (p.styleNo || '').trim().toLowerCase() === query.trim().toLowerCase()
    );
    const isDisplayingKnownProduct = fullDisplayMatch || exactStyleNoMatch;
    
    if (filtered.length === 0 && !isDisplayingKnownProduct) {
      onUpdate({ ...lineData, style: query });
    }
  };

  const handleStyleSelect = (product) => {
    if (!product) return;
    
    const fullStyleDisplay = `${product.styleNo} - ${product.shortDescription || product.description}`;
    const defaultSizeGrid = (product.defaultSizeGrid || '').trim();
    const sizeType = defaultSizeGrid && SIZES[defaultSizeGrid] ? defaultSizeGrid : (lineData.sizeType || 'ADULT');
    const sizes = (defaultSizeGrid && SIZES[defaultSizeGrid])
      ? Object.fromEntries(SIZES[defaultSizeGrid].map(size => [size, lineData.sizes?.[size] ?? '']))
      : lineData.sizes || {};
    
    onUpdate({
      ...lineData,
      style: fullStyleDisplay,
      brand: product.brand || '',
      styleNo: product.styleNo || '',
      cogs: product.cogs ?? lineData.cogs,
      sizeType,
      sizes
    });
    setSelectedSizeType(sizeType);
    setSearchQuery(fullStyleDisplay);
    setFilteredProducts([]);
  };

  const handleAddProduct = async (productData) => {
    try {
      const productsRef = collection(db, 'products');
      const docRef = await addDoc(productsRef, {
        ...productData,
        createdAt: new Date()
      });
      
      // Update the products list
      const newProduct = { id: docRef.id, ...productData };
      setProducts(prevProducts => [...prevProducts, newProduct]);
      
      // Update the line item with the new product
      handleStyleSelect(newProduct);
      
      // Clear the search results
      setFilteredProducts([]);
      setSearchQuery('');
      setShowAddProduct(false);
    } catch (error) {
      console.error('Error adding product:', error);
      alert('Error adding product. Please try again.');
    }
  };

  // Handle drag and drop for artwork
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // Handle direct mockup upload (bypasses FilesModal for quick mockup adds)
  const handleMockupUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Please drop an image file');
      return;
    }

    try {
      let fileUrl;
      const fileExtension = file.name.split('.').pop();
      
      // Determine storage path
      const designNameSanitized = (lineData.design || lineData.designName || 'design').replace(/[^a-zA-Z0-9_-]/g, '_');
      const storageId = lineId || lineData.docId || lineData.id;
      
      if (orderNumber && designNameSanitized) {
        // Upload to Firebase Storage
        const storagePath = storageId 
          ? `artwork/lines/${storageId}/mockup.${fileExtension}`
          : `artwork/${orderNumber}/${designNameSanitized}/Mockups/mockup.${fileExtension}`;
        
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        fileUrl = await getDownloadURL(storageRef);
        console.log('✅ Uploaded mockup to:', storagePath);
        
        // Update Firestore with the mockup URL
        if (storageId) {
          try {
            const lineRef = doc(db, 'orderLines', storageId);
            await updateDoc(lineRef, {
              'files.mockup': {
                url: fileUrl,
                fileName: file.name
              },
              designFileUrl: fileUrl,
              updatedAt: serverTimestamp()
            });
            console.log('✅ Updated Firestore with mockup URL');
          } catch (error) {
            console.error('Error updating Firestore with mockup URL:', error);
          }
        }
      } else {
        // No order number yet - create object URL for preview
        // IMPORTANT: Store the File object so it can be uploaded when order is saved
        fileUrl = URL.createObjectURL(file);
        console.log('⚠️ No order number - using preview URL. File will be uploaded on save.');
      }
      
      // Update local state
      setArtworkPreviewUrl(fileUrl);
      
      // Notify parent component
      // Store the File object temporarily so it can be uploaded when order is saved
      onUpdate({
        ...lineData,
        files: {
          ...(lineData.files || {}),
          mockup: {
            url: fileUrl,
            fileName: file.name,
            file: file // Store File object for later upload
          }
        },
        designFileUrl: fileUrl,
        _mockupFile: file // Also store separately for easy access during save
      });

      await syncDesignMockupIndex(fileUrl, file.name);
      
    } catch (error) {
      console.error('Error uploading mockup:', error);
      alert('Failed to upload mockup: ' + error.message);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer?.files?.[0];
    if (file) {
      await handleMockupUpload(file);
    }
  };

  // Calculate total quantity
  const totalQuantity = useMemo(() => {
    if (!lineData.sizes) return 0;
    return Object.values(lineData.sizes).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
  }, [lineData.sizes]);

  // Get sizes for current type
  const currentSizes = useMemo(() => {
    return SIZES[selectedSizeType]?.sort(sortSizes) || [];
  }, [selectedSizeType]);

  // Effect to initialize Design Only with quantity of 1 when missing (allow 0 if user sets it)
  useEffect(() => {
    if (lineData.type === LINE_TYPES.DESIGN_ONLY) {
      const hasGarmentFields = (lineData.style || '').trim() || (lineData.color || '').trim();
      if (hasGarmentFields) return;

      const hasDesign = lineData.sizes && typeof lineData.sizes.Design !== 'undefined';
      if (!hasDesign) {
        onUpdate({
          ...lineData,
          sizes: { Design: 1 }
        });
      }
    }
  }, [lineData.type, lineData.sizes, onUpdate]);

  // Effect to handle DTG/DTF artwork status based on file manager
  useEffect(() => {
    const handleArtworkStatus = async () => {
      if ((lineData.type === LINE_TYPES.DTG || lineData.type === LINE_TYPES.DTF) && 
          lineData.status === LINE_STATUSES.ORDERED) {
        
        // For DTG/DTF: Check file manager production files
        const productionFiles = lineData.files?.production || [];
        // Use printLocations array for DTG/DTF
        const selectedLocations = (lineData.printLocations || []).map(loc => `${loc}-production`);
        
        const hasAnyArtwork = selectedLocations.some(locationType => 
          productionFiles.some(file => file.type === locationType)
        );

        // Update CUT status if we have artwork and aren't already CUT
        if (hasAnyArtwork && !lineData.isCut) {
          const newStatus = lineData.isIn ? LINE_STATUSES.READY_TO_PRINT : LINE_STATUSES.CUT_NOT_IN;
          const updatedData = {
            ...lineData,
            isCut: true,
            status: newStatus,
            dtgArtworkReady: lineData.type === LINE_TYPES.DTG,
            dtfArtworkReady: lineData.type === LINE_TYPES.DTF
          };
          onUpdate(updatedData);
        }
        // Reset CUT status if we have no artwork but are marked as CUT
        else if (!hasAnyArtwork && lineData.isCut) {
          const updatedData = {
            ...lineData,
            isCut: false,
            status: LINE_STATUSES.ORDERED,
            dtgArtworkReady: false,
            dtfArtworkReady: false
          };
          onUpdate(updatedData);
        }
      }
    };

    handleArtworkStatus();
  }, [
    lineData.type,
    lineData.status,
    lineData.printLocations,
    lineData.files?.production,
    lineData.isCut,
    onUpdate
  ]);

  return (
    <ThemeProvider theme={theme}>
      <StyledCard elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
        {(onMoveUp || onMoveDown) && (
          <ReorderRail>
            {onMoveUp ? (
              <Tooltip title="Move line up">
                <span>
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onMoveUp();
                    }}
                    disabled={!canMoveUp}
                    sx={reorderIconButtonSx}
                  >
                    <ExpandLessIcon />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {onMoveDown ? (
              <Tooltip title="Move line down">
                <span>
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onMoveDown();
                    }}
                    disabled={!canMoveDown}
                    sx={reorderIconButtonSx}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </ReorderRail>
        )}
      <CardContent sx={{ p: 0, flex: 1, minWidth: 0 }}>
        <MainGrid>
          {/* Design Name */}
          <StyleSearchContainer>
            <StyledTextField
              fullWidth
              label="Design Name"
              value={lineData.design || ''}
              onChange={(e) => handleDesignNameChange(e.target.value)}
              onFocus={() => {
                if (customerId && customerDesignLibrary.length > 0) {
                  setShowDesignLibrary(true);
                }
              }}
              onBlur={() => {
                designLibraryBlurTimeoutRef.current = setTimeout(() => {
                  setShowDesignLibrary(false);
                }, 180);
              }}
              variant="outlined"
              size="small"
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
            {customerId && designLibraryLoading && (
              <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.45)', mt: 0.5 }}>
                Loading previous designs…
              </Typography>
            )}
            {showDesignLibrary && filteredDesignLibrary.length > 0 && (
              <StyleSearchResults>
                {filteredDesignLibrary.map((entry) => (
                  <StyleSearchResult
                    key={entry.id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleDesignLibrarySelect(entry)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {entry.mockupUrl ? (
                        <Box
                          component="img"
                          src={entry.mockupUrl}
                          alt=""
                          sx={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0, opacity: 0.85 }}
                        />
                      ) : null}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                          {entry.displayName}
                        </Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.55)' }}>
                          {getDesignLibrarySubtitle(entry)}
                        </Typography>
                      </Box>
                    </Box>
                  </StyleSearchResult>
                ))}
              </StyleSearchResults>
            )}
          </StyleSearchContainer>

          {/* Print Type */}
          <StyledFormControl fullWidth size="small">
            <InputLabel>Print Type</InputLabel>
            <StyledSelect
              value={lineData.type || LINE_TYPES.SCREEN_PRINT}
              onChange={(e) => onUpdate({ ...lineData, type: e.target.value })}
              label="Print Type"
              {...SELECT_MENU_PROPS}
            >
              {Object.entries(LINE_TYPES).map(([key, value]) => (
                <MenuItem key={key} value={value}>{value}</MenuItem>
              ))}
            </StyledSelect>
          </StyledFormControl>

          {/* Style Number - Hidden for Design Only */}
          {lineData.type !== LINE_TYPES.DESIGN_ONLY && (
            <StyleSearchContainer>
              <StyledTextField
                fullWidth
                label="Style"
                value={searchQuery}
                onChange={handleStyleSearch}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setFilteredProducts([]);
                    setSearchQuery('');
                    onUpdate({ ...lineData, style: '' });
                  }
                }}
                variant="outlined"
                size="small"
                inputProps={{
                  style: { textTransform: 'uppercase' }
                }}
              />
              {(filteredProducts.length > 0 || (searchQuery && !products.some(product =>
                `${(product.styleNo || '')} - ${product.shortDescription || product.description || ''}`.trim() === searchQuery.trim()
              ) && !products.some(p => (p.styleNo || '').trim().toLowerCase() === searchQuery.trim().toLowerCase()))) && (
                <StyleSearchResults>
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map(product => (
                      <StyleSearchResult
                        key={product.id}
                        onClick={() => handleStyleSelect(product)}
                      >
                        {product.styleNo} - {product.shortDescription || product.description}
                      </StyleSearchResult>
                    ))
                  ) : searchQuery && (
                    <AddNewButton onClick={() => setShowAddProduct(true)}>
                      + Add New Product
                    </AddNewButton>
                  )}
                </StyleSearchResults>
              )}
            </StyleSearchContainer>
          )}

          {/* Color - Hidden for Design Only */}
          {lineData.type !== LINE_TYPES.DESIGN_ONLY && (
            <StyledTextField
              fullWidth
              label="Color"
              value={lineData.color || ''}
              onChange={(e) => onUpdate({ ...lineData, color: e.target.value })}
              variant="outlined"
              size="small"
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
            />
          )}

          {/* Design Description - Only for Design Only */}
          {lineData.type === LINE_TYPES.DESIGN_ONLY && (
            <StyledTextField
              fullWidth
              label="Design Description"
              value={lineData.designDescription || ''}
              onChange={(e) => onUpdate({ ...lineData, designDescription: e.target.value })}
              variant="outlined"
              size="small"
            />
          )}

          {/* Quantity - Only for Design Only */}
          {lineData.type === LINE_TYPES.DESIGN_ONLY && (
            <StyledTextField
              fullWidth
              label="Quantity"
              type="number"
              value={lineData.sizes?.Design ?? ''}
              onChange={(e) => {
                const qty = e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0);
                onUpdate({
                  ...lineData,
                  sizes: { Design: qty === '' ? 0 : qty }
                });
              }}
              variant="outlined"
              size="small"
              InputProps={{ inputProps: { min: 0 } }}
            />
          )}

          {/* Price - Only for Design Only */}
          {lineData.type === LINE_TYPES.DESIGN_ONLY && (
            <StyledTextField
              fullWidth
              label="Price"
              value={lineData.price || ''}
              onChange={(e) => onUpdate({ ...lineData, price: e.target.value })}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          )}

          {/* Action Buttons — expand + delete only (reorder lives in left rail) */}
          <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end', alignItems: 'center' }}>
            <IconButton
              onClick={() => setExpanded(!expanded)}
              sx={{ 
                color: 'white', 
                padding: 0,
                height: '32px',
                width: '32px'
              }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <IconButton
              size="small"
              onClick={onDelete}
              sx={{
                color: 'white',
                height: '32px',
                width: '32px',
                '&:hover': {
                  color: 'var(--al-primary)'
                }
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </MainGrid>

        {/* Expandable Section */}
        <Collapse in={expanded}>
          <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
            {/* Left side: Artwork and Print Locations */}
            <Box sx={{ flex: 1 }}>
              <PrintLocationsGrid>
                {/* Artwork Upload - Show for all types except Design Only */}
                {lineData.type !== LINE_TYPES.DESIGN_ONLY && (
                <Box sx={{ position: 'relative' }}>
                  {(artworkPreviewUrl || getMockupUrl(lineData)) ? (
                    // Show mockup/artwork preview (artworkPreviewUrl can be blob for pending uploads)
                    <ArtworkUploadBox
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFilesModal(true);
                      }}
                      className={isDragging ? 'dragging' : ''}
                      sx={{ height: '120px', position: 'relative', overflow: 'hidden', cursor: 'pointer' }}
                    >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                          onClick={(e) => {
                            // Open FilesModal when clicking the mockup image
                            e.stopPropagation();
                            setShowFilesModal(true);
                          }}
                        >
                          <img 
                            src={artworkPreviewUrl || getMockupUrl(lineData)} 
                            alt="Mockup" 
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', pointerEvents: 'none' }}
                          />
                          <IconButton
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              borderRadius: 0,
                              padding: '4px',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.7)'
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Remove mockup from files if it exists, otherwise remove old artwork fields
                              if (lineData.files?.mockup) {
                                onUpdate({
                                  ...lineData,
                                  files: {
                                    ...lineData.files,
                                    mockup: null
                                  }
                                });
                              } else {
                                onUpdate({
                                  ...lineData,
                                  designFileUrl: null,
                                  artworkFile: null,
                                  dtgArtworkReady: false,
                                  dtgArtworkUrl: null,
                                  artworkUrl: null
                                });
                                setArtworkPreviewUrl(null);
                              }
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: '1rem', color: 'white' }} />
                          </IconButton>
                        </Box>
                    </ArtworkUploadBox>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <UploadFilesButton
                      onClick={(e) => {
                        e.stopPropagation();
                        document.getElementById(`mockup-input-${lineData.id || index}`).click();
                      }}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={isDragging ? 'dragging' : ''}
                      sx={customerId ? { height: '84px' } : undefined}
                    >
                      <input
                        id={`mockup-input-${lineData.id || index}`}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleMockupUpload(file);
                          e.target.value = ''; // Reset input
                        }}
                      />
                      <CloudUploadIcon sx={{ fontSize: '1.5rem' }} />
                      <Typography sx={{ fontSize: '0.75rem' }}>
                        Add Mockup
                      </Typography>
                    </UploadFilesButton>
                    {customerId && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMockupLibrary(true);
                        }}
                        sx={{
                          color: 'var(--al-primary)',
                          border: '1px solid rgba(var(--al-primary-rgb), 0.4)',
                          borderRadius: 0,
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          py: 0.5,
                          '&:hover': {
                            backgroundColor: 'rgba(var(--al-primary-rgb), 0.12)'
                          }
                        }}
                      >
                        Browse library
                      </Button>
                    )}
                    </Box>
                  )}
                </Box>
                )}

                {/* Print Locations - Hidden for Design Only */}
                {lineData.type !== LINE_TYPES.DESIGN_ONLY && (() => {
                  const isDTG = lineData.type === LINE_TYPES.DTG;
                  const isEmbroidery = lineData.type === LINE_TYPES.EMBROIDERY;
                  const isDTF = lineData.type === LINE_TYPES.DTF;
                  // Screen Print is the default, so also check for undefined/null type
                  const isScreenPrint = !lineData.type || lineData.type === LINE_TYPES.SCREEN_PRINT || lineData.type === 'screen' || lineData.type === 'Screen Print';
                  
                  // DTG & DTF: Show "File Uploads" section with location checkboxes
                  if (isDTG || isDTF) {
                    return (
                      <Box sx={{ gridColumn: 'span 3' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', mb: 1, textTransform: 'uppercase' }}>
                          Print Locations
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {['Front', 'Back', 'Other'].map((location) => {
                            const locationKey = location.toLowerCase();
                            const printLocations = lineData.printLocations || [];
                            const isLocationSelected = printLocations.includes(locationKey);
                            
                            // Check file manager for production files matching this location
                            const productionFiles = lineData.files?.production || [];
                            const locationProductionType = `${locationKey}-production`;
                            const hasProductionFiles = productionFiles.some(file => file.type === locationProductionType);

                            return (
                              <Box key={location}>
                                <LocationCheckboxContainer>
                                  <SharpCheckbox
                                    type="checkbox"
                                    checked={isLocationSelected}
                                    onChange={(e) => {
                                      const currentLocations = lineData.printLocations || [];
                                      const newLocations = e.target.checked
                                        ? [...currentLocations, locationKey]
                                        : currentLocations.filter(loc => loc !== locationKey);
                                      onUpdate({
                                        ...lineData,
                                        printLocations: newLocations
                                      });
                                    }}
                                  />
                                  <Typography sx={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'white' }}>
                                    {location}
                                  </Typography>
                                  {/* Status indicator - shows Ready/Artwork Needed when location is selected */}
                                  {isLocationSelected && (
                                    <VerificationIndicator 
                                      verified={hasProductionFiles} 
                                      needsArtwork={!hasProductionFiles}
                                      sx={{ marginLeft: 'auto' }}
                                    >
                                      {hasProductionFiles ? (
                                        <>
                                          <CheckCircleIcon sx={{ fontSize: '0.9rem', color: 'var(--al-primary)' }} />
                                          <Typography sx={{ fontSize: '0.65rem', textTransform: 'uppercase', color: 'var(--al-primary)' }}>
                                            Ready
                                          </Typography>
                                        </>
                                      ) : (
                                        <>
                                          <WarningIcon sx={{ fontSize: '0.9rem', color: '#FFC107' }} />
                                          <Typography sx={{ fontSize: '0.65rem', textTransform: 'uppercase', color: '#FFC107' }}>
                                            Artwork Needed
                                          </Typography>
                                        </>
                                      )}
                                    </VerificationIndicator>
                                  )}
                                </LocationCheckboxContainer>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  }
                  
                  // Screen Print & Embroidery: Show color/thread inputs for each location
                  return ['Front', 'Back', 'Other'].map((location) => {
                    const locationKey = location.toLowerCase();
                    const colorsArrayKey = `${locationKey}InkColors`;
                    const colorLabel = isEmbroidery ? 'Thread' : 'Color';
                    const locationLabel = isEmbroidery ? `${location} Threads` : `${location} Inks`;

                    return (
                      <Box key={location}>
                        <StyledFormControl fullWidth size="small">
                          <InputLabel>{locationLabel}</InputLabel>
                          <StyledSelect
                            value={lineData[colorsArrayKey]?.length || 0}
                            onChange={(e) => {
                              const count = parseInt(e.target.value);
                              onUpdate({
                                ...lineData,
                                [colorsArrayKey]: Array(count).fill('')
                              });
                            }}
                            label={locationLabel}
                            {...SELECT_MENU_PROPS}
                          >
                            <MenuItem value={0}>0 {location}</MenuItem>
                            <MenuItem value={1}>1 {location}</MenuItem>
                            <MenuItem value={2}>2 {location}</MenuItem>
                            <MenuItem value={3}>3 {location}</MenuItem>
                            <MenuItem value={4}>4 {location}</MenuItem>
                            <MenuItem value={5}>5 {location}</MenuItem>
                            <MenuItem value={6}>6 {location}</MenuItem>
                            <MenuItem value={7}>7 {location}</MenuItem>
                            <MenuItem value={8}>8 {location}</MenuItem>
                          </StyledSelect>
                        </StyledFormControl>
                        
                        {/* Color/Thread inputs */}
                        <ColorInputsContainer>
                          {Array.from({ length: lineData[colorsArrayKey]?.length || 0 }).map((_, idx) => {
                            const key = `${location}-${idx}`;
                            const searchQuery = inkSearchQueries[key] || '';
                            const filteredInksForThisInput = filteredInks[key] || [];
                            const lookupCount = isEmbroidery ? threads.length : inks.length;
                            const showResults = searchQuery.length > 0 && filteredInksForThisInput.length > 0 && lookupCount > 0;
                            const currentInkValue = lineData[colorsArrayKey]?.[idx] || '';
                            const showEyedropper = !currentInkValue;
                            
                            return (
                              <InkSearchContainer key={`${location}-color-${idx}`}>
                                <StyledTextField
                                  size="small"
                                  placeholder={`${colorLabel} ${idx + 1}`}
                                  value={lineData[colorsArrayKey]?.[idx] || ''}
                                  onChange={(e) => {
                                    applyInkInputValue(location, idx, e.target.value);
                                  }}
                                  onFocus={(e) => {
                                    // Show lookup results if there's already a query
                                    const currentValue = e.target.value;
                                    if (currentValue.length > 0 && ((isScreenPrint && inks.length > 0) || (isEmbroidery && threads.length > 0))) {
                                      handleInkSearch(location, idx, currentValue);
                                    }
                                  }}
                                  onBlur={() => {
                                    // Delay hiding results to allow clicking on them
                                    const timeout = setTimeout(() => {
                                      setFilteredInks(prev => {
                                        const newFiltered = { ...prev };
                                        delete newFiltered[key];
                                        return newFiltered;
                                      });
                                      setInkSearchQueries(prev => {
                                        const newQueries = { ...prev };
                                        delete newQueries[key];
                                        return newQueries;
                                      });
                                    }, 200);
                                    inkSearchTimeoutRef.current[key] = timeout;
                                  }}
                                  inputProps={{
                                    style: { textTransform: 'uppercase' }
                                  }}
                                  InputProps={{
                                    endAdornment: showEyedropper ? (
                                      <InputAdornment position="end" sx={{ mr: -0.5 }}>
                                        <Tooltip title="Pick color from screen">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handlePickColor(location, idx);
                                            }}
                                            sx={{
                                              color: 'var(--al-primary)',
                                              width: 22,
                                              height: 22,
                                              '&:hover': { backgroundColor: 'rgba(var(--al-primary-rgb), 0.1)' }
                                            }}
                                          >
                                            <ColorizeIcon sx={{ fontSize: '0.85rem' }} />
                                          </IconButton>
                                        </Tooltip>
                                      </InputAdornment>
                                    ) : null
                                  }}
                                  sx={{ width: '140px' }}
                                />
                                {/* Ink/Thread autocomplete dropdown */}
                                {showResults && (
                                  <InkSearchResults>
                                    {filteredInksForThisInput.map(ink => (
                                      <InkSearchResult
                                        key={ink.id}
                                        onClick={() => handleInkSelect(location, idx, ink)}
                                      >
                                        <ColorSwatch hex={ink.hexCode} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                                          <span>
                                            {isEmbroidery
                                              ? `Thread ${ink.threadNumber || ''}${ink.name ? ` - ${ink.name}` : ''}`
                                              : ink.name}
                                          </span>
                                          {typeof ink._deltaE === 'number' && (
                                            <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>ΔE {ink._deltaE.toFixed(2)}</span>
                                          )}
                                        </Box>
                                      </InkSearchResult>
                                    ))}
                                  </InkSearchResults>
                                )}
                              </InkSearchContainer>
                            );
                          })}
                        </ColorInputsContainer>
                      </Box>
                    );
                  });
                })()}

              </PrintLocationsGrid>
            </Box>

            {/* Right side: Size Type, Price, COGS, and Sizes */}
            <Box sx={{ width: '500px' }}>
              {lineData.type === LINE_TYPES.DESIGN_ONLY ? (
                // Design Only - All fields are in the header above
                null
              ) : (
                // Regular print types - Full right side
                <>
                  <RightSideGrid>
                    {/* Size Type */}
                    <StyledFormControl size="small">
                      <InputLabel>Size Type</InputLabel>
                      <StyledSelect
                        value={selectedSizeType}
                        onChange={(e) => {
                          const newSizeType = e.target.value;
                          setSelectedSizeType(newSizeType);
                          // Reset all size quantities and create empty object with new size type's sizes
                          const newSizes = Object.fromEntries(
                            SIZES[newSizeType].map(size => [size, ''])
                          );
                          onUpdate({
                            ...lineData,
                            sizeType: newSizeType,
                            sizes: newSizes
                          });
                        }}
                        label="Size Type"
                        {...SELECT_MENU_PROPS}
                      >
                        {Object.keys(SIZES).map((type) => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </StyledSelect>
                    </StyledFormControl>

                    {/* Price */}
                    <StyledTextField
                      label="Price"
                      value={lineData.price || ''}
                      onChange={(e) => onUpdate({ ...lineData, price: e.target.value })}
                      variant="outlined"
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />

                    {/* COGS */}
                    <StyledTextField
                      label="COGS"
                      value={lineData.cogs || ''}
                      onChange={(e) => onUpdate({ ...lineData, cogs: e.target.value })}
                      variant="outlined"
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </RightSideGrid>

                  {/* Size Grid */}
                  <Box sx={{ mt: 1 }}>
                    <SizeGrid>
                      {currentSizes.map((size) => (
                        <StyledTextField
                          key={size}
                          label={size}
                          type="number"
                          value={lineData.sizes?.[size] || ''}
                          onChange={(e) => {
                            const newSizes = {
                              ...(lineData.sizes || {}),
                              [size]: e.target.value
                            };
                            onUpdate({ ...lineData, sizes: newSizes });
                          }}
                          variant="outlined"
                          size="small"
                          InputProps={{ inputProps: { min: 0 } }}
                        />
                      ))}
                      <StyledTextField
                        label="Total"
                        value={totalQuantity}
                        disabled
                        variant="outlined"
                        size="small"
                      />
                    </SizeGrid>
                  </Box>
                </>
              )}
            </Box>
          </Box>
          
          {/* Ink Notes + Press Setup — Screen Print and Embroidery */}
          {(!lineData.type ||
            lineData.type === LINE_TYPES.SCREEN_PRINT || 
            lineData.type === 'screen' || 
            lineData.type === 'Screen Print' ||
            lineData.type === LINE_TYPES.EMBROIDERY) && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {(!lineData.type ||
                lineData.type === LINE_TYPES.SCREEN_PRINT ||
                lineData.type === 'screen' ||
                lineData.type === 'Screen Print') && (
                <PressSetupIndicatorButton
                  lineData={lineData}
                  inkCatalog={inks}
                  onClick={() => setShowPressSetupModal(true)}
                />
              )}
              <Button
                onClick={() => setShowInkNotesModal(true)}
                sx={{
                  backgroundColor: lineData.inkNotes ? 'rgba(var(--al-primary-rgb), 0.15)' : '#363636',
                  color: lineData.inkNotes ? 'var(--al-primary)' : 'rgba(255, 255, 255, 0.7)',
                  border: lineData.inkNotes ? '1px solid rgba(var(--al-primary-rgb), 0.4)' : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 0,
                  padding: '6px 12px',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  fontFamily: "'Montserrat', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  '&:hover': {
                    backgroundColor: lineData.inkNotes ? 'rgba(var(--al-primary-rgb), 0.25)' : '#404040',
                    borderColor: 'var(--al-primary)'
                  }
                }}
              >
                {lineData.inkNotes ? '📝' : '+'} 
                {lineData.inkNotes ? 'View Ink Notes' : 'Add Ink Notes'}
              </Button>
              {lineData.inkNotes && (
                <Typography 
                  sx={{ 
                    fontSize: '0.65rem', 
                    color: 'rgba(255, 255, 255, 0.4)',
                    fontStyle: 'italic',
                    maxWidth: '300px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  "{lineData.inkNotes.substring(0, 50)}{lineData.inkNotes.length > 50 ? '...' : ''}"
                </Typography>
              )}
            </Box>
          )}

          {/* Duplicate Line Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, pr: 1 }}>
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDuplicate(lineData);
              }}
              sx={{ 
                color: 'var(--al-primary)',
                '&:hover': {
                  backgroundColor: 'rgba(var(--al-primary-rgb), 0.1)'
                }
              }}
              size="small"
            >
              <ContentCopyIcon />
            </IconButton>
          </Box>
        </Collapse>
      </CardContent>
      </Box>

      <AddProduct
        isOpen={showAddProduct}
        onClose={() => setShowAddProduct(false)}
        onAdd={handleAddProduct}
        initialData={{ styleNo: searchQuery }}
      />
      <FilesModal
        isOpen={showFilesModal}
        onClose={() => setShowFilesModal(false)}
        orderNumber={orderNumber}
        designName={lineData.design || lineData.designName}
        lineId={lineId || lineData.docId || lineData.id}
        lineData={lineData}
        customerId={customerId}
        orderId={orderId}
        onFilesUpdate={(updatedLineData) => {
          onUpdate(updatedLineData);
          const mockupUrl = getMockupUrl(updatedLineData);
          if (mockupUrl) {
            syncDesignMockupIndex(mockupUrl, updatedLineData.files?.mockup?.fileName);
          }
        }}
      />
      <CustomerMockupsModal
        isOpen={showMockupLibrary}
        onClose={() => setShowMockupLibrary(false)}
        customerId={customerId}
        hasExistingMockup={!!(artworkPreviewUrl || getMockupUrl(lineData) || lineData.files?.mockup?.url)}
        onSelect={handleSelectLibraryMockup}
      />
      <InkNotesModal
        isOpen={showInkNotesModal}
        onClose={() => setShowInkNotesModal(false)}
        inkNotes={lineData.inkNotes}
        designName={lineData.design || lineData.designName}
        onSave={(notes) => onUpdate({ ...lineData, inkNotes: notes })}
      />
      <PressSetupModal
        isOpen={showPressSetupModal}
        onClose={() => setShowPressSetupModal(false)}
        lineData={lineData}
        pressSetup={lineData.pressSetup}
        designName={lineData.design || lineData.designName}
        inkCatalog={inks}
        onSave={(pressSetup) => onUpdate({ ...lineData, pressSetup })}
      />
    </StyledCard>
    </ThemeProvider>
  );
};

export default LineItem2;